/**
 * Count tiles for the payment-intents page; each one is the State filter
 * for its state. Counts come from the list endpoint's ``total``
 * (``page_size=1``), with the page's other filters applied but not the
 * state, so a tile always says what clicking it would show.
 *
 * A failed count shows "–", never 0: zero failed payments and "couldn't
 * load the count" must not look alike.
 */
import { useQueries } from "@tanstack/react-query";
import { CircleAlert, CircleCheck, CircleX, Hourglass, Layers, type LucideIcon } from "lucide-react";
import { getPaymentIntents, type IntentState, type PaymentIntentListFilters } from "@/lib/api";
import { cn } from "@/lib/utils";

const TILES: ReadonlyArray<{
  state: IntentState | null;
  label: string;
  icon: LucideIcon;
  tone: string;
}> = [
  { state: null, label: "All", icon: Layers, tone: "bg-primary/10 text-primary" },
  { state: "requires_action", label: "Needs action", icon: CircleAlert, tone: "bg-warning/10 text-warning" },
  { state: "processing", label: "Processing", icon: Hourglass, tone: "bg-info/10 text-info" },
  { state: "succeeded", label: "Succeeded", icon: CircleCheck, tone: "bg-success/10 text-success" },
  { state: "failed", label: "Failed", icon: CircleX, tone: "bg-destructive/10 text-destructive" },
];

export const TILE_STATES: ReadonlySet<IntentState> = new Set(
  TILES.flatMap((t) => (t.state ? [t.state] : [])),
);

export function IntentStateTiles({
  filters,
  active,
  onSelect,
  refetchInterval,
}: {
  filters: Omit<PaymentIntentListFilters, "status" | "page" | "page_size">;
  active: IntentState | undefined;
  onSelect: (state: IntentState | null) => void;
  refetchInterval: number;
}) {
  const counts = useQueries({
    queries: TILES.map((tile) => ({
      queryKey: ["payment-intents", "count", { ...filters, status: tile.state }] as const,
      queryFn: () =>
        getPaymentIntents({ ...filters, status: tile.state ?? undefined, page_size: 1 }),
      select: (r: { total: number }) => r.total,
      refetchInterval,
    })),
  });

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {TILES.map((tile, i) => {
        const selected = (active ?? null) === tile.state;
        const count = counts[i];
        const Icon = tile.icon;
        return (
          <button
            key={tile.label}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(selected ? null : tile.state)}
            className={cn(
              "flex items-center gap-3 rounded-lg border bg-card p-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              selected && "border-primary ring-1 ring-primary",
            )}
          >
            <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", tile.tone)}>
              <Icon className="h-5 w-5" aria-hidden />
            </span>
            {/* The label comes first for screen readers; the figure shows on top. */}
            <span className="flex min-w-0 flex-col-reverse">
              <span className="truncate text-xs text-muted-foreground">{tile.label}</span>
              <span className="text-xl font-semibold leading-tight tabular-nums">
                {count?.data ?? "–"}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
